import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { Upload, FileVideo, X, CheckCircle, AlertCircle } from 'lucide-react';
import { projectAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const VideoUploader = ({ onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    setError(null);
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError('Please upload a video file');
      return;
    }

    // Validate file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      setError('File size must be under 100MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 10;
        });
      }, 200);

      // Upload to backend
      const project = await projectAPI.uploadVideo(file, file.name);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setUploadedFile({
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
        project: project
      });
      
      toast({
        title: "Upload successful!",
        description: "Your video has been uploaded and is being processed.",
      });
      
    } catch (error) {
      console.error('Upload failed:', error);
      setError(error.response?.data?.detail || 'Upload failed. Please try again.');
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.response?.data?.detail || "Please try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setUploadProgress(0);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const proceedToEditor = () => {
    if (uploadedFile && onUploadComplete) {
      onUploadComplete(uploadedFile);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-800">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Upload Video</h1>
              <p className="text-gray-400 text-sm">Start your AI-powered editing journey</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => window.history.back()}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="max-w-2xl mx-auto">

      <Card 
        className={`relative p-12 border-2 border-dashed bg-gray-900 transition-all duration-300 ${
          dragActive 
            ? 'border-purple-500 bg-purple-900/20 shadow-2xl shadow-purple-500/20' 
            : 'border-gray-700 hover:border-purple-400 hover:shadow-xl hover:shadow-purple-500/10'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {!uploadedFile ? (
          <>
            <div className="flex flex-col items-center justify-center space-y-6">
              <div className="p-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg">
                <Upload className="w-12 h-12 text-white" />
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2 text-white">Upload Your Video</h3>
                <p className="text-gray-400 mb-4">
                  Drag & drop your video file here, or click to browse
                </p>
                <p className="text-sm text-gray-500">
                  Supports MP4, MOV, AVI • Max 2 minutes • Under 100MB
                </p>
              </div>

              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 text-lg font-semibold transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Choose File
              </Button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="video/*"
              onChange={handleFileInput}
            />
          </>
        ) : (
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center space-x-4 p-4 bg-green-900/20 border border-green-600/30 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div className="flex-1 text-left">
                <p className="font-semibold text-white">{uploadedFile.name}</p>
                <p className="text-sm text-gray-400">
                  {(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={removeFile}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <video 
              src={uploadedFile.url} 
              controls 
              className="w-full max-w-md mx-auto rounded-lg shadow-lg"
            />

            <Button 
              onClick={proceedToEditor}
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-8 py-4 text-lg font-semibold transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              Start Editing with AI ✨
            </Button>
          </div>
        )}

        {isUploading && (
          <div className="absolute inset-0 bg-gray-900/95 backdrop-blur-sm flex flex-col items-center justify-center rounded-lg">
            <div className="w-full max-w-xs space-y-4">
              <div className="flex items-center space-x-3">
                <FileVideo className="w-6 h-6 text-purple-400" />
                <span className="font-medium text-white">Uploading video...</span>
              </div>
              <Progress value={uploadProgress} className="h-3" />
              <p className="text-sm text-gray-400 text-center">
                {Math.round(uploadProgress)}% complete
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-900/20 border border-red-600/30 rounded-lg flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        )}
      </Card>

      <div className="mt-8 text-center">
        <p className="text-gray-400">
          ✨ <strong className="text-white">Pro tip:</strong> Upload vertical videos (9:16) for best social media results
        </p>
      </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUploader;